import L from 'leaflet';
import dateParse from 'date-fns/parse';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';
import {exclusionCategory} from './ExclusionCategoryEnum';
import {BusMainTypeEnum} from './BusMainTypeEnum';
import {BusJsonDataEnum} from './BusJsonDataEnum';
import {BusFormDataEnum} from './BusFormDataEnum';
import {BusColorEnum} from './BusColorEnum';
import {
  mapGroup,
  mapMarkerExtraordinaryLockout,
  mapTrackCircle,
  mapTrainCircleMarker,
  mapBusCircleMarker,
  renderExclusions
} from './mapShapes';
import {
  busPopupTemplate,
  extraordinaryPopupTemplate
} from './mapTemplates';
import WebComponent from 'fg-frontend/src/webComponents/WebComponent';
import isEqual from 'lodash/lang/isEqual';
import groupBy from 'lodash/collection/groupBy';
import difference from 'lodash/array/difference';
import isEmpty from 'lodash/lang/isEmpty';
import $ from 'jquery';
// https://codepen.io/tarekraafat/pen/rQopdW
import AutoComplete from '@tarekraafat/autocomplete.js';

const MapTypeEnum = {
  TRAIN: 'train',
  BUS: 'bus'
};

const isNumeric = (value) => {
  if (typeof value != 'string') {
    return false;
  }
  return !isNaN(value) && !isNaN(parseFloat(value));
};

const isDateValidForShown = (validFrom, validTo, dateFrom, dateTo) => {
  const oneDayMinusSecond = (1000 * 60 * 60 * 24) - 1000 * 60;
  const fetureDate = 9999999999999;
  dateFrom = Array.isArray(dateFrom) || dateFrom == null ? 0 : dateFrom;
  dateTo = Array.isArray(dateTo) || dateTo == null ? fetureDate : dateTo + oneDayMinusSecond;
  validTo = Array.isArray(validTo) || validTo == null ? fetureDate : validTo;
  return areIntervalsOverlapping(
    {start: validFrom, end: validTo},
    {start: dateFrom, end: dateTo},
    {inclusive: true}
  );
};

const getIds = (items) => {
  return items.map(({id}) => id);
};

const renderTracks = ({map, data}) => {
  const group = L.featureGroup().addTo(map);
  data.forEach((trackData) => {
    const subGroup = mapGroup({
      data: trackData,
      lineColor: BusColorEnum.GREEN,
      lineBorderColor: BusColorEnum.WHITE,
      icon: (point, pointData) => mapTrackCircle(point, pointData),
      map
    });
    group.addLayer(subGroup);
  });
  return group;
};

const getAllPositionData = ({map, data}) => {
  const cords = [];
  data.forEach(({latitude, longitude}) => {
    cords.push([latitude, longitude]);
  });
  return cords;
};

const setURLParam = (key, value) => {
  const url = new URL(window.location.href);
  if (value === '' || value === BusMainTypeEnum.EXPRESS) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.pushState({path: url.href}, '', url.href);
};

const getURLParam = (key) => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(key);
};

const FRAGMENTS = {
  CUSTOM_POPUP: '[data-fragment="Map.customPopup"]',
  CLOSE_CUSTOM_POPUP: '[data-fragment="Map.closeCustomPopup"]',
  ALERT_MESSAGE: '[data-fragment="Map.alertMessage"]',
  RESET_FILTER_BUTTON: '[data-fragment="Map.resetFilterButton"]'
};

const BUS_FRAGMENTS = {
  RESET_REGION_BUTTON: '[data-fragment="Map.resetRegionAutocomplete"]',
  RESET_LINK_NUMBER_BUTTON: '[data-fragment="Map.resetLinkNumberAutocomplete"]'
};

const TRAIN_INPUT_FRAGMENTS = {
  actualCheckbox: '[name="actual"]',
  plannedCheckbox: '[name="planned"]',
  extraCheckbox: '[name="extra"]',
  fromInput: '[name="dateFrom"]',
  toInput: '[name="dateTo"]'
};

const BUS_INPUT_FRAGMENTS = {
  mainType: '[name="mainType"]',
  type: '[name="type"]'
};

const AUTOCOMPLETE_CONFIG = {
  wrapper: true,
  data: {
    src: []
  },
  resultsList: {
    element: (list, data) => {
      if (!data.results.length) {
        const message = document.createElement('li');
        message.setAttribute('class', 'no_result');
        message.innerHTML = `Nebyla nalezena žádná shoda s: <strong>"${data.query}"</strong>`;
        list.prepend(message);
      }
    },
    noResults: true,
    maxResults: undefined
  },
  resultItem: {
    highlight: true
  },
  debounce: 250
};

const REGION_AUTOCOMPLETE_CONFIG = {
  name: 'regionAutoComplete',
  selector: '#regionAutoComplete',
  placeHolder: 'Hledejte spoje podle oblasti'
};

const LINK_NUMBER_AUTOCOMPLETE_CONFIG = {
  name: 'linkNumberAutocomplete',
  selector: '#linkNumberAutocomplete',
  placeHolder: 'Zadejte cílovou stanici nebo číslo linky',
  resultItem: {
    element: (item, data) => {
      item.style = 'display: flex; justify-content: space-between; gap: 2rem';
      item.innerHTML = `
      <span class="category">
        ${data.match}
      </span>
      <span class="sub-category">
        ${data.value[BusJsonDataEnum.DESTINATION_NAME]}
      </span>`;
    },
    highlight: true
  }
};

export default class Map extends WebComponent {
  constructor(props) {
    super(props);

    if (this.props.type === MapTypeEnum.TRAIN) {
      this.state = {
        formData: {
          actual: true, planned: true, extra: true, dateFrom: null, dateTo: null
        },
        extraordinaryData: [],
        actualExclusionData: [],
        plannedExclusionData: [],
        tracksData: [],
        visibleIds: [],
        hiddenIds: [],
        oltisData: [],
        stationNameEnum: {}
      };
      this.props.exclusionData = typeof this.props.exclusionData === 'string' ? JSON.parse(this.props.exclusionData) : this.props.exclusionData;
    } else {
      this.regionAutoComplete = null;
      this.defaultRadioMainTypeOption = BusMainTypeEnum.EXPRESS;
      this.radioMainTypeOption = getURLParam(BusFormDataEnum.MAIN_TYPE) || this.defaultRadioMainTypeOption;
      this.defaultDestinationName = getURLParam(BusFormDataEnum.DESTINATION_NAME);
      this.defaultRegion = getURLParam(BusFormDataEnum.TYPE);
      this.defaultLinkNumber = getURLParam(BusFormDataEnum.LINK_NUMBER);
      this.firstAutoZoom = false;
      this.state = {
        formData: {
          destinationName: this.defaultDestinationName,
          mainType: this.radioMainTypeOption,
          type: this.defaultRegion,
          linkNumber: this.defaultLinkNumber
        },
        popupData: null,
        oltisData: [],
        filteredOltisData: [],
        filteredPopupData: []
      };
    }

    this.handleResetFilterClick = this.handleResetFilterClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleClosePopup = this.handleClosePopup.bind(this);
    this.handleRegionAutocompleteSelection = this.handleRegionAutocompleteSelection.bind(this);
    this.handleRegionAutocompleteFocus = this.handleRegionAutocompleteFocus.bind(this);
    this.handleRegionAutocompletePressButton = this.handleRegionAutocompletePressButton.bind(this);
    this.handleLinkNumberAutocompleteSelection = this.handleLinkNumberAutocompleteSelection.bind(this);
    this.handleLinkNumberAutocompleteFocus = this.handleLinkNumberAutocompleteFocus.bind(this);
    this.handleLinkNumberAutocompletePressButton = this.handleLinkNumberAutocompletePressButton.bind(this);
    this.handleResetRegionClick = this.handleResetRegionClick.bind(this);
    this.handleResetLinkNumberClick = this.handleResetLinkNumberClick.bind(this);
  }

  didMount() {
    const {
      formData
    } = this.state;
    const mapCanvas = this.element.querySelector('.map__canvas');

    const map = L.map(mapCanvas, {
      scrollWheelZoom: false
    }).setView([50.08804, 14.42076], 8);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);
    this.map = map;

    this.setState(this.getFilteredData(formData));
    this.loadOltisData();
    this.oltisInterval = setInterval(() => {
      this.loadOltisData();
    }, this.props.type === MapTypeEnum.TRAIN ? 30000 : 15000);

    if (this.props.type === MapTypeEnum.TRAIN) {
      this.loadStationData();
    } else {
      const {
        destinationName,
        mainType,
        type,
        linkNumber
      } = formData;
      this.regionAutoComplete = new AutoComplete({...AUTOCOMPLETE_CONFIG, ...REGION_AUTOCOMPLETE_CONFIG});
      this.linkNumberAutoComplete = new AutoComplete({...AUTOCOMPLETE_CONFIG, ...LINK_NUMBER_AUTOCOMPLETE_CONFIG});
      this.resetFilterButton = this.element.querySelector(FRAGMENTS.RESET_FILTER_BUTTON);
      this.resetRegionButton = this.element.querySelector(BUS_FRAGMENTS.RESET_REGION_BUTTON);
      this.resetLinkNumberButton = this.element.querySelector(BUS_FRAGMENTS.RESET_LINK_NUMBER_BUTTON);
      this.regionInputContainer = this.resetRegionButton.closest('.map__filter__input');
      this.linkNumberInputContainer = this.resetLinkNumberButton.closest('.map__filter__input');

      const radioMainTypeCheckedElement = this.element.querySelector(`${BUS_INPUT_FRAGMENTS.mainType}[value="${mainType}"]`);
      if (radioMainTypeCheckedElement) {
        radioMainTypeCheckedElement.checked = true;
      }
      if (this.regionAutoComplete) {
        this.regionAutoComplete.input.value = type;
      }
      if (this.linkNumberAutoComplete) {
        this.linkNumberAutoComplete.input.value = linkNumber || destinationName;
      }
    }
    this.bindListeners();
  }

  bindListeners() {
    const {
      type
    } = this.props;

    const inputFragments = type === MapTypeEnum.TRAIN ? TRAIN_INPUT_FRAGMENTS : BUS_INPUT_FRAGMENTS;

    Object.keys(inputFragments).forEach((key) => {
      this.on('change', inputFragments[key], this.handleInputChange);
    });

    if (type === MapTypeEnum.BUS) {
      this.on('click', FRAGMENTS.RESET_FILTER_BUTTON, this.handleResetFilterClick);
      this.on('click', BUS_FRAGMENTS.RESET_REGION_BUTTON, this.handleResetRegionClick);
      this.on('click', BUS_FRAGMENTS.RESET_LINK_NUMBER_BUTTON, this.handleResetLinkNumberClick);

      if (this.regionAutoComplete) {
        this.regionAutoComplete.input.addEventListener('selection', this.handleRegionAutocompleteSelection);
        this.regionAutoComplete.input.addEventListener('focus', this.handleRegionAutocompleteFocus);
        this.regionAutoComplete.input.addEventListener('keyup', this.handleRegionAutocompletePressButton);
      }
      if (this.linkNumberAutoComplete) {
        this.linkNumberAutoComplete.input.addEventListener('selection', this.handleLinkNumberAutocompleteSelection);
        this.linkNumberAutoComplete.input.addEventListener('focus', this.handleLinkNumberAutocompleteFocus);
        this.linkNumberAutoComplete.input.addEventListener('keyup', this.handleLinkNumberAutocompletePressButton);
      }
    }
  }

  getFilteredData(formData) {
    const {
      type
    } = this.props;

    if (type === MapTypeEnum.TRAIN) {
      const {exclusionData} = this.props;
      const {
        planned,
        extra,
        actual,
        dateFrom,
        dateTo
      } = formData;

      const nowTime = new Date().getTime();
      const allIds = getIds(exclusionData);
      const extraordinaryData = extra ? exclusionData.filter(({category, date, validTo}) => {
        return category === exclusionCategory.EXTRAORDINARY && isDateValidForShown(date, validTo, dateFrom, dateTo);
      }) : [];
      const plannedExclusionData = planned ? exclusionData.filter(({category, date, validTo}) => {
        return category === exclusionCategory.EXCLUSIONS && nowTime < date && isDateValidForShown(date, validTo, dateFrom, dateTo);
      }) : [];
      const actualExclusionData = actual ? exclusionData.filter(({category, date, validTo}) => {
        return category === exclusionCategory.EXCLUSIONS && nowTime >= date && isDateValidForShown(date, validTo, dateFrom, dateTo);
      }) : [];
      const visibleIds = [...getIds(extraordinaryData), ...getIds(plannedExclusionData), ...getIds(actualExclusionData)];
      const hiddenIds = difference(allIds, visibleIds);

      return {
        extraordinaryData,
        actualExclusionData,
        plannedExclusionData,
        visibleIds,
        hiddenIds
      };
    } else {
      const {
        destinationName,
        mainType,
        type,
        linkNumber
      } = formData;

      let filteredOltisData = this.state.oltisData.filter(data => data.mainType === mainType);

      if (this.regionAutoComplete) {
        const groupByType = Object.values(groupBy(filteredOltisData, BusFormDataEnum.TYPE));
        const regions = [...groupByType.map(item => item[0].type)];
        this.regionAutoComplete.data = {
          src: regions
        };
      }

      if (!isEmpty(type)) {
        filteredOltisData = filteredOltisData.filter(data => data.type === type);
      }

      if (this.linkNumberAutoComplete) {
        const linkNumber = [...filteredOltisData].map(({destinationName, linkNumberAlias}) => {
          return {
            [BusFormDataEnum.LINK_NUMBER]: linkNumberAlias,
            [BusFormDataEnum.DESTINATION_NAME]: destinationName
          };
        });
        this.linkNumberAutoComplete.data = {
          src: linkNumber,
          keys: [BusFormDataEnum.DESTINATION_NAME, BusFormDataEnum.LINK_NUMBER],
          filter: (list) => {
            let inputValue;
            let isNumber = false;

            if (list[0] && list[0].match !== undefined) {
              const inputDataFromArrayList = [...list][0].match;
              const regEx =  new RegExp('\<mark>(.*?)<\/mark>', 'g');
              inputValue = regEx.exec(inputDataFromArrayList);
              isNumber = isNumeric(inputValue[1]);
            }

            if(isNumber) {
              return list;
            } else {
              return Array.from(
                new Set(list.map(value => {
                  return value.match;
                }))
              ).map((result) => {
                return list.find((value) => value.match === result);

              });
            }
          }
        };
      }

      // Tady si dát pozor na podmínky (linkNumber a destinationName)
      // neměla by nastat kolize (měla by se vždy pustit jen jedna), protože to nastavuju ručně v setLinkNumber
      if (!isEmpty(linkNumber)) {
        filteredOltisData = filteredOltisData.filter(data => data.linkNumberAlias === linkNumber);
      }

      if (!isEmpty(destinationName)) {
        filteredOltisData = filteredOltisData.filter(data => data.destinationName === destinationName);
      }

      return {
        filteredOltisData
      };
    }
  }

  setLinkNumber(value = '') {
    let newFormData;

    if (isNumeric(value)) {
      setURLParam(BusFormDataEnum.DESTINATION_NAME, '');
      setURLParam(BusFormDataEnum.LINK_NUMBER, value);
      newFormData = {
        ...this.state.formData,
        [BusFormDataEnum.DESTINATION_NAME]: '',
        [BusFormDataEnum.LINK_NUMBER]: value
      };
    } else {
      setURLParam(BusFormDataEnum.DESTINATION_NAME, value);
      setURLParam(BusFormDataEnum.LINK_NUMBER, '');
      newFormData = {
        ...this.state.formData,
        [BusFormDataEnum.DESTINATION_NAME]: value,
        [BusFormDataEnum.LINK_NUMBER]: ''
      };
    }

    const data = this.getFilteredData(newFormData);

    this.setState({
      formData: newFormData,
      ...data
    });
  }

  setType(value = '') {
    setURLParam(BusFormDataEnum.TYPE, value);

    const newFormData = {
      ...this.state.formData,
      [BusFormDataEnum.TYPE]: value
    };

    const data = this.getFilteredData(newFormData);

    this.setState({
      formData: newFormData,
      ...data
    });
  }

  handleRegionAutocompleteSelection(event) {
    const feedback = event.detail;
    this.regionAutoComplete.input.blur();
    const selectedValue = feedback.selection.value;
    this.regionAutoComplete.input.value = selectedValue;
    this.resetLinkNumber();
    this.setType(selectedValue);
  }

  handleRegionAutocompleteFocus() {
    if (this.regionAutoComplete.input.value.length) {
      this.regionAutoComplete.start();
    }
  }

  handleRegionAutocompletePressButton(e) {
    if (e.key === 'Enter') {
      const inputValue = this.regionAutoComplete.input.value;
      this.regionAutoComplete.close();
      this.resetLinkNumber();
      this.setType(inputValue);
    }
  }

  handleLinkNumberAutocompleteSelection(event) {
    let selectedValue;
    const feedback = event.detail;
    this.linkNumberAutoComplete.input.blur();
    if (isNumeric(feedback.query)) {
      selectedValue = feedback.selection.value[BusFormDataEnum.LINK_NUMBER];
    } else {
      selectedValue = feedback.selection.value[BusFormDataEnum.DESTINATION_NAME];
    }
    this.linkNumberAutoComplete.input.value = selectedValue;
    this.setLinkNumber(selectedValue);
  }

  handleLinkNumberAutocompleteFocus() {
    if (this.linkNumberAutoComplete.input.value.length) {
      this.linkNumberAutoComplete.start();
    }
  }

  handleLinkNumberAutocompletePressButton(e) {
    if (e.key === 'Enter') {
      const inputValue = this.linkNumberAutoComplete.input.value;
      this.linkNumberAutoComplete.close();
      this.setLinkNumber(inputValue);
    }
  }

  resetFilter() {
    const {
      formData
    } = this.state;

    const {
      destinationName,
      type,
      linkNumber
    } = formData;

    if (!isEmpty(type)) {
      this.resetRegion();
    }
    if (!isEmpty(linkNumber) || !isEmpty(destinationName)) {
      this.resetLinkNumber();
    }
  }

  resetLinkNumber() {
    this.linkNumberAutoComplete.input.value = '';
    this.setLinkNumber();
  }

  resetRegion() {
    this.regionAutoComplete.input.value = '';
    this.setType();
  }

  handleResetFilterClick() {
    this.resetFilter();
  }

  handleResetLinkNumberClick() {
    this.resetLinkNumber();
  }

  handleResetRegionClick() {
    this.resetFilter();
  }

  handleInputChange({target: {value, name, checked, type}}) {
    let newValue = null;

    switch (type) {
    case 'text':
      if (this.props.type === MapTypeEnum.TRAIN) {
        const {
          dateFormat
        } = this.props;
        newValue = dateParse(value, dateFormat, new Date());
        const isValidDate = !isNaN(newValue);
        if (!isValidDate) {
          newValue = null;
        } else {
          newValue = newValue.getTime();
        }
      }
      break;
    case 'radio':
      newValue = value;
      setURLParam(BusFormDataEnum.MAIN_TYPE, value);
      this.resetFilter();
      break;
    case 'checkbox':
      newValue = checked;
      break;
    default:
      break;
    }

    const newFormData = {
      ...this.state.formData,
      [name]: type = newValue
    };

    const data = this.getFilteredData(newFormData);

    this.setState({
      formData: newFormData,
      ...data
    });
  }

  handleClosePopup(e) {
    const mapPopupElement = this.element.querySelector(FRAGMENTS.CUSTOM_POPUP);
    mapPopupElement.style.display = 'none';
    e.target.removeEventListener('click', this.handleClosePopup);
    this.setState({filteredPopupData: []});
  }

  loadOltisData() {
    const {
      type
    } = this.props;

    let normalizedData;
    const jsonUrl = type === MapTypeEnum.TRAIN ? '/trainPositions.json' : '/bus/telmax.json';
    $.ajax({
      url: jsonUrl,
      dataType: 'json',
      cache: false,
      success: (data) => {
        if (type === MapTypeEnum.TRAIN) {
          normalizedData = Object.values(groupBy(data, 'stopId'));
        } else {
          normalizedData = data;
        }
        this.setState({
          oltisData: normalizedData
        });
      }
    });
  }

  loadStationData() {
    $.ajax({
      url: '/js/json/station.json',
      dataType: 'json',
      cache: true,
      success: (data) => {
        this.setState({
          stationNameEnum: data
        });
      }
    });
  }

  updateOltisShapes() {
    const map = this.map;

    if (this.oltisGroup) {
      this.oltisGroup.eachLayer((marker) => {
        marker.closePopup();
      });
      map.removeLayer(this.oltisGroup);
      this.oltisGroup = null;
    }
    const group = L.featureGroup().addTo(map);
    const {
      oltisData,
      stationNameEnum
    } = this.state;

    oltisData.forEach((popupData) => {
      const {latitude, longitude} = popupData[0];
      const circleData = {
        point: [parseFloat(latitude), parseFloat(longitude)],
        popupData,
        stationNameEnum
      };
      const trainCircle = mapTrainCircleMarker(circleData).addTo(map);
      group.addLayer(trainCircle);
    });
    this.oltisGroup = group;
  }

  updateMapShapes(prevState) {
    const {
      type
    } = this.props;

    const map = this.map;

    if (this.tracksGroup) {
      map.removeLayer(this.tracksGroup);
    }

    if (type === MapTypeEnum.TRAIN) {
      const {
        extraordinaryData,
        actualExclusionData,
        plannedExclusionData
      } = this.state;

      const {
        tracksData
      } = this.props;

      if (this.extraGroup) {
        map.removeLayer(this.extraGroup);
      }
      if (this.plannedExclusionGroup) {
        map.removeLayer(this.plannedExclusionGroup);
      }
      if (this.actualExclusionGroup) {
        map.removeLayer(this.actualExclusionGroup);
      }
      this.extraGroup = renderExclusions({
        map,
        icon: mapMarkerExtraordinaryLockout,
        iconTooltip: (data) => {
          return data.stops[0].name;
        },
        data: extraordinaryData,
        lineColor: BusColorEnum.YELLOW,
        lineBorderColor: BusColorEnum.WHITE,
        popup: (data) => {
          return extraordinaryPopupTemplate(data);
        }
      });
      this.plannedExclusionGroup = renderExclusions({
        map,
        data: plannedExclusionData,
        lineColor: BusColorEnum.PRIMARY,
        lineBorderColor: BusColorEnum.WHITE,
        popup: (data) => {
          return extraordinaryPopupTemplate(data);
        }
      });
      this.actualExclusionGroup = renderExclusions({
        map,
        data: actualExclusionData,
        lineColor: BusColorEnum.LILA,
        lineBorderColor: BusColorEnum.WHITE,
        popup: (data) => {
          return extraordinaryPopupTemplate(data);
        }
      });

      this.tracksGroup = renderTracks({map, data: tracksData});
      map.fitBounds(this.tracksGroup.getBounds(), {padding: [3, 3]});
    } else {
      const {
        filteredOltisData,
        formData
      } = this.state;

      const {
        formData: prevFormData
      } = prevState;

      const enableAutoZoom = prevFormData == null || (!isEqual(formData, prevFormData) || !this.firstAutoZoom);

      const group = L.featureGroup().addTo(map);

      if (this.oltisGroup) {
        this.oltisGroup.eachLayer((marker) => {
          marker.closePopup();
        });

        map.removeLayer(this.oltisGroup);
        this.oltisGroup = null;
      }

      filteredOltisData.forEach((filteredData) => {
        const {latitude, longitude} = filteredData;

        const circleData = {
          point: [parseFloat(latitude), parseFloat(longitude)],
          filteredData
        };

        const busCircle = mapBusCircleMarker(circleData, (filteredPopupData) => {
          this.setState({filteredPopupData: filteredPopupData});
        }).addTo(map);

        group.addLayer(busCircle);
      });

      this.oltisGroup = group;
      this.tracksGroup = getAllPositionData({map, data: filteredOltisData});

      if (this.tracksGroup.length > 0 && enableAutoZoom) {
        map.fitBounds(this.tracksGroup, {padding: [3, 3]});
        this.firstAutoZoom = true;
      }
    }
  }

  updateListItems() {
    const {
      visibleIds,
      hiddenIds
    } = this.state;
    visibleIds.forEach((id) => {
      const element = document.querySelector(`[data-id="${id}"]`);
      if (element) {
        element.style.display = 'block';
      }
    });
    hiddenIds.forEach((id) => {
      const element = document.querySelector(`[data-id="${id}"]`);
      if (element) {
        element.style.display = 'none';
      }
    });
  }

  updatePopup() {
    const {
      filteredPopupData
    } = this.state;

    const isVisible = !isEmpty(filteredPopupData);
    const mapPopupElement = this.element.querySelector(FRAGMENTS.CUSTOM_POPUP);
    const template = isVisible ? `
<div class="map__custom-popup__wrapper">${busPopupTemplate(filteredPopupData)}</div>
<button class="map__custom-popup__close" data-fragment="Map.closeCustomPopup"><span class="map__custom-popup__close-text">Zavřít</span></button>
` : '';
    mapPopupElement.style.display = isVisible ? 'block' : 'none';
    mapPopupElement.innerHTML = template;
    const closeButtonElement = this.element.querySelector(FRAGMENTS.CLOSE_CUSTOM_POPUP);
    if (closeButtonElement) {
      closeButtonElement.addEventListener('click', this.handleClosePopup);
    }
  }

  updateFilteredData() {
    const {
      formData
    } = this.state;
    const data = this.getFilteredData(formData);
    this.setState({
      ...data
    });
  }

  updateAlertMessage() {
    const {
      filteredOltisData
    } = this.state;

    const isVisible = isEmpty(filteredOltisData);
    const mapAlertMessageElement = this.element.querySelector(FRAGMENTS.ALERT_MESSAGE);
    mapAlertMessageElement.style.display = isVisible ? 'flex' : 'none';
  }

  updateFilterInputs() {
    const {
      formData
    } = this.state;

    const {
      mainType,
      type
    } = formData;

    const isRegionOrMhd = mainType === BusMainTypeEnum.REGION || mainType === BusMainTypeEnum.MHD;

    if (this.regionInputContainer) {
      if (isRegionOrMhd) {
        this.regionInputContainer.style.display = 'block';
      } else {
        this.regionInputContainer.style.display = 'none';
      }
    }

    if (this.linkNumberInputContainer) {
      if ((isRegionOrMhd && type) || !isRegionOrMhd) {
        this.linkNumberInputContainer.style.display = 'block';
      } else {
        this.linkNumberInputContainer.style.display = 'none';
      }
    }
  }

  updateFilterButtons() {
    const {
      formData
    } = this.state;

    const {
      destinationName,
      type,
      linkNumber
    } = formData;

    const isRegionVisible = !isEmpty(type);
    const isLinkNumberVisible = !isEmpty(linkNumber) || !isEmpty(destinationName);
    const isFilterButtonVisible = isLinkNumberVisible || isRegionVisible;

    this.resetRegionButton.style.display = isRegionVisible ? 'block' : 'none';
    this.resetLinkNumberButton.style.display = isLinkNumberVisible ? 'block' : 'none';
    this.resetFilterButton.style.display = isFilterButtonVisible ? 'block' : 'none';
  }

  updateColor() {
    const {
      formData
    } = this.state;

    const {
      mainType
    } = formData;

    const root = document.documentElement;
    const cssVariableColorSchema = '--color-leafletmaps-schema';
    const cssVariableColorMarkerText = '--color-leafletmaps-marker-text';
    const cssVariableColorMarkerTextActive = '--color-leafletmaps-marker-text-active';

    switch (mainType) {
    case BusMainTypeEnum.EXPRESS:

      root.style.setProperty(cssVariableColorSchema, BusColorEnum.LILA);
      root.style.setProperty(cssVariableColorMarkerText, BusColorEnum.WHITE);
      root.style.setProperty(cssVariableColorMarkerTextActive, BusColorEnum.LILA);
      break;
    default:
      root.style.setProperty(cssVariableColorSchema, BusColorEnum.PRIMARY);
      root.style.setProperty(cssVariableColorMarkerText, BusColorEnum.WHITE);
      root.style.setProperty(cssVariableColorMarkerTextActive, BusColorEnum.PRIMARY);
      break;
    }
  }

  update(prevProps, prevState, isInDocument) {
    const {
      type
    } = this.props;

    if (type === MapTypeEnum.TRAIN) {
      const {
        oltisData,
        visibleIds,
        stationNameEnum
      } = this.state;

      const {
        oltisData: prevOltisData,
        visibleIds: prevVisibleIds,
        stationNameEnum: prevStationNameEnum
      } = prevState;

      if (!isEqual(visibleIds, prevVisibleIds)) {
        this.updateListItems();
        this.updateMapShapes(null);
      }
      if (
        isInDocument &&
        (
          !isEqual(oltisData, prevOltisData || []) ||
          !isEqual(stationNameEnum, prevStationNameEnum || [])
        )
      ) {
        this.updateOltisShapes();
      }
    } else {
      const {
        filteredOltisData,
        oltisData
      } = this.state;

      const {
        filteredOltisData: prevFilteredOltisData,
        oltisData: prevOltisData
      } = prevState;

      if (!isEqual(filteredOltisData, prevFilteredOltisData)) {
        this.updateColor();
        this.updateAlertMessage();
        this.updateMapShapes(prevState);
      }

      if (!isEqual(oltisData, prevOltisData)) {
        this.updateFilteredData();
      }

      this.updatePopup();
      this.updateFilterButtons();
      this.updateFilterInputs();
    }
  }
}

Map.defaultProps = {
  dateFormat: 'd. M. yyyy'
};



// WEBPACK FOOTER //
// ./fe/web-default/src/js/components/Map/Map.js